import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar.tsx";
import { Footer } from "@/components/layout/Footer.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import { useToast } from "@/hooks/use-toast.ts";
import { useUserAuth } from "@/hooks/useUserAuth.ts";

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { isAuthenticated, signInWithGoogle, signInWithOtp } = useUserAuth();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const authError = searchParams.get("authError");

  const handleEmailSignIn = async () => {
    if (!email.trim()) {
      toast({
        title: t("userAuth.emailRequiredTitle"),
        description: t("userAuth.emailRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await signInWithOtp(email.trim());
      toast({
        title: t("userAuth.checkInboxTitle"),
        description: t("userAuth.checkInboxDescription"),
      });
    } catch (error) {
      toast({
        title: t("userAuth.signInFailedTitle"),
        description: error instanceof Error ? error.message : t("userAuth.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      toast({
        title: t("userAuth.googleSignInFailedTitle"),
        description: error instanceof Error ? error.message : t("userAuth.tryAgain"),
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-2">
          <CardHeader>
            <CardTitle className="font-pixel text-sm uppercase">{t("userAuth.signIn")}</CardTitle>
            <CardDescription>
              {t("userAuth.signInDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {authError
              ? (
                <p className="text-sm text-destructive">
                  {t("userAuth.callbackErrorPrefix")} {decodeURIComponent(authError)}
                </p>
              )
              : null}
            {isAuthenticated
              ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">{t("userAuth.alreadySignedIn")}</p>
                  <Button onClick={() => navigate("/", { replace: true })} className="w-full">
                    {t("userAuth.goToStories")}
                  </Button>
                </div>
              )
              : (
                <>
                  <Button
                    className="w-full"
                    onClick={handleGoogleSignIn}
                    disabled={isSubmitting}
                  >
                    {t("userAuth.continueWithGoogle")}
                  </Button>

                  <div className="text-center text-xs text-muted-foreground uppercase tracking-wide">
                    {t("userAuth.orUseEmail")}
                  </div>

                  <Input
                    type="email"
                    placeholder={t("userAuth.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleEmailSignIn}
                    disabled={isSubmitting}
                  >
                    {t("userAuth.sendMagicLink")}
                  </Button>
                </>
              )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
